import { AlertCircle, Clock, Eye, EyeOff, Loader2, LogIn } from 'lucide-react';
import { useState } from 'react';
import { HelpHint } from '../components/HelpHint';
import { OutboundLink } from '../components/LeavingDialog';
import { useSession } from '../lib/session';
import { ForgotPasswordPage } from './ForgotPasswordPage';
import { ProviderButtons } from '../components/ProviderButtons';
import { SplitScreen } from '../components/SplitScreen';
import { COMPANY_SITE, COMPANY_SITE_LABEL } from '../lib/site';

export function LoginPage() {
  const { signIn } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  /**
   * Why they are looking at this page, if they did not ask to be.
   *
   * Read once and cleared. A session that ended on its own puts a sentence here
   * on its way out; without it, somebody who was working a moment ago is
   * returned to a bare sign-in form with no explanation, and the obvious
   * conclusion is that the application threw them out for no reason.
   */
  const [whyHere] = useState<string | null>(() => {
    try {
      const reason = sessionStorage.getItem('gsn:signed-out-reason');
      if (reason) sessionStorage.removeItem('gsn:signed-out-reason');
      return reason;
    } catch {
      return null;
    }
  });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  /**
   * Show the password as typed.
   *
   * Off to begin with, and never remembered between visits. A phone keyboard
   * on a bright site is where passwords get mistyped, and the alternative to
   * this is the thing people actually do — type it into the email box to
   * check it, then retype it.
   */
  const [showPassword, setShowPassword] = useState(false);
  /**
   * Keep this session going for thirty days on this device.
   *
   * Unticked by default, deliberately. The safe answer has to be the one
   * somebody gets by not reading, and the long session is a choice a person
   * makes about a device they own.
   */
  const [remember, setRemember] = useState(false);
  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password, { remember });

      /**
       * Land where they were going, not on the board.
       *
       * Somebody who followed a share link and was asked to sign in wants that
       * ticket, and making them find it again is the kind of small rudeness
       * that adds up. Only same-origin paths are honoured: `next` arrives in a
       * URL anyone can write, and an open redirect on a sign-in page is how a
       * convincing phishing link gets built out of your own domain.
       */
      const next = new URLSearchParams(window.location.search).get('next');
      if (next && next.startsWith('/') && !next.startsWith('//')) {
        window.location.assign(next);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not sign in.');
    } finally {
      /*
       * Always, not only on the way out through the catch.
       *
       * The button is disabled while this is true, so any path that leaves it
       * set is a form nobody can use again - and the one that did was the
       * success path, which returned without clearing it and relied on the
       * application re-rendering this screen away. When that was slow, the
       * screen stayed, the button stayed dead, and the only way on was a
       * reload.
       */
      setBusy(false);
    }
  }

  if (forgot) return <ForgotPasswordPage onBack={() => setForgot(false)} />;

  return (
    <SplitScreen>
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-5">
          <div className="lg:hidden">
            <OutboundLink href={COMPANY_SITE} title={COMPANY_SITE_LABEL}>
              <img
                src="/brand/logo.png"
                alt={`GoSpeedNet — visit ${COMPANY_SITE_LABEL}`}
                className="h-9 w-auto"
              />
            </OutboundLink>
          </div>

          <div>
            <h2 className="font-heading text-2xl">Sign in</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Welcome back. Sign in to pick up where you left off — whatever your job here,
              this is the way in.
            </p>
          </div>

          {/* Not an error — nothing went wrong. Navy, not red. */}
          {whyHere && !error && (
            <p
              className="flex items-start gap-2 rounded-xl border border-navy-200 bg-navy-50
                px-3 py-2 text-sm text-navy-700"
              role="status"
            >
              <Clock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{whyHere}</span>
            </p>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-xl bg-danger-50 p-3 text-sm text-danger-600"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="you@gospeednet.co.ke"
            />
          </div>

          <div>
            <label htmlFor="password" className="label">
              Password
              <HelpHint>
                Five failed attempts locks this account for 15 minutes. Ask an admin to reset it
                rather than guessing further.
              </HelpHint>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pr-11"
              />
              {/*
                Inside the field rather than beside it, so it is where a thumb
                already is on a phone. `tabIndex={-1}` keeps it out of the path
                from password to Sign in — tabbing into a show/hide button on
                the way to submitting is a small daily irritation.
              */}
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground
                  hover:text-foreground"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <Eye className="h-4 w-4" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          <label className="flex items-start gap-2.5 text-sm">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border text-primary-600
                focus:ring-primary-500"
            />
            <span>
              Keep me signed in on this device
              <span className="block text-xs text-muted-foreground">
                Thirty days instead of twelve hours. Only on a phone or computer that is yours —
                not on a shared machine.
              </span>
            </span>
          </label>

          <button type="submit" disabled={busy} className="btn-primary w-full">
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <LogIn className="h-4 w-4" aria-hidden="true" />
            )}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          {/*
            Every role, not just staff. A technician locked out of their account
            at seven in the morning should not have to reach an admin to start
            their day.
          */}
          <button
            type="button"
            onClick={() => setForgot(true)}
            className="w-full text-center text-sm text-muted-foreground hover:text-primary-700 hover:underline"
          >
            Forgotten your password?
          </button>

          <ProviderButtons />
        </form>
    </SplitScreen>
  );
}
